import ExcelJS from 'exceljs';
import { TDcfScenarioParams } from './dcfCalculations';

// ── Style tokens (mirrors exampleExcelExport.ts) ─────────────────────────────────
const BRANDDARK = '00355f';   // titles, section headers, KPI label text
const MIDBLUE   = '569FD3';   // input fills + section divider rules
const ORANGE    = 'DF6721';   // headline KPI fills
const BODYTEXT  = '151B1E';   // all body text (not pure black)
const WHITE     = 'FFFFFF';

// Number formats — units live in row labels, NOT in the cell
const MONEY     = '#,##0.00';                     // $ amounts (label says "$ Mn")
const MONEY_CF  = '#,##0.00;-#,##0.00;"-"';       // cash flows: minus-sign neg, dash zero
const PCT_IN    = '0.0%';                          // % inputs
const PCT_CALC  = '0.00%';                         // % derived / results
const INT       = '0';                             // day counts
const FACTOR    = '0.00';                        // discount factors

// Uniform row height (points) — change this one value to restyle every row
const ROW_H     = 19;

// ── Helpers ────────────────────────────────────────────────────────────────────
function colLetter(zeroIdx: number): string {
	let result = '';
	let n = zeroIdx + 1;
	while (n > 0) {
		result = String.fromCharCode(65 + (n - 1) % 26) + result;
		n = Math.floor((n - 1) / 26);
	}
	return result;
}

function buildQuarterLabels(n: number): { label: string; days: number }[] {
	const now = new Date();
	let q = Math.floor(now.getMonth() / 3) + 1, y = now.getFullYear();
	return Array.from({ length: n }, () => {
		const m = (q - 1) * 3;
		const days = Math.round((new Date(y, m + 3, 0).getTime() - new Date(y, m, 1).getTime()) / 86400000) + 1;
		const label = `Q${q} ${y}`;
		if (++q > 4) { q = 1; y++; }
		return { label, days };
	});
}

async function svgToPng(svgStr: string, w: number, h: number): Promise<string | null> {
	return new Promise(resolve => {
		const canvas = document.createElement('canvas');
		canvas.width = w; canvas.height = h;
		const ctx = canvas.getContext('2d');
		if (!ctx) { resolve(null); return; }
		const img = new Image();
		const blob = new Blob([svgStr], { type: 'image/svg+xml' });
		const url = URL.createObjectURL(blob);
		img.onload = () => { ctx.drawImage(img, 0, 0, w, h); URL.revokeObjectURL(url); resolve(canvas.toDataURL('image/png').split(',')[1]); };
		img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
		img.src = url;
	});
}

// ── Export ─────────────────────────────────────────────────────────────────────
export async function exportDcfToExcel(
	params: TDcfScenarioParams,
	vesselName: string,
	logoSvg?: string,
): Promise<void> {
	const nQ = params.numQuarters;
	const quarters = buildQuarterLabels(nQ);
	const qC = (t: number) => colLetter(t + 1);   // t=0→B, t=1→C, ...
	const lastC = qC(nQ);
	const lastCol = nQ + 2;                          // total columns (A + data)

	// ── Row layout registry ──────────────────────────────────────────────────────
	// Single source of truth for every row number. Rows are allocated sequentially:
	// remove/reorder an entry here (or a GAP) and every label AND formula reference
	// below shifts automatically, because all formulas cite these vars, never literals.
	let _r = 0;
	const N = () => ++_r;          // allocate the next row
	const GAP = (k = 1) => { _r += k; };   // leave k blank spacer row(s)

	const rTitle = N();  GAP(); const rCaption = N();  GAP();

	const rInputsHdr    = N();
	const rPurchase     = N();
	const rSale         = N();
	const rEarnings     = N();
	const rOpex         = N();
	const rCoe          = N();
	const rLtv          = N();
	const rBalloon      = N();
	const rBaseRate     = N();
	const rMargin       = N();
	const rOnHire       = N();
	const rOperatingDays = N();
	GAP();

	const rDerivedHdr   = N();
	const rWacc         = N();   // derived from LTV, CoE, base rate + margin
	const rQWacc        = N();
	const rQCoe         = N();
	const rQIntRate     = N();
	const rLoanAmt      = N();
	const rEquityInv    = N();
	const rQPrincipal   = N();
	GAP();

	const rCashHdr      = N();
	const rPeriod       = N();
	const rQuarter      = N();
	const rDays         = N();
	const rRevenue      = N();
	const rOpCosts      = N();
	const rNetOpCF      = N();
	const rPurchSale    = N();
	const rProjCF       = N();
	const rDFWacc       = N();
	const rPVProjCF     = N();
	GAP();

	const rProjHdr      = N();
	const rProjNPV      = N();
	const rProjDCF      = N();
	const rProjIRR      = N();
	GAP();

	const rFinHdr       = N();
	const rLoanDraw     = N();
	const rLoanOpen     = N();
	const rInterest     = N();
	const rPrincipalRepay = N();
	const rBalloonPay   = N();
	const rEquityCF     = N();
	const rDFCoe        = N();
	const rPVEquityCF   = N();
	GAP();

	const rEquityHdr    = N();
	const rEquityNPV    = N();
	const rEquityDCF    = N();
	const rEquityIRR    = N();
	GAP();

	const rAnalHdr      = N();
	const rS1           = N();
	const rS2           = N();
	const rO            = N();
	const rCoefA        = N();
	const rConstB       = N();

	// Column-B reference helpers (relative / absolute)
	const b  = (row: number) => `B${row}`;
	const ab = (row: number) => `$B$${row}`;

	// ── Workbook ───────────────────────────────────────────────────────────────
	const wb = new ExcelJS.Workbook();
	wb.creator = 'MSI';
	const ws = wb.addWorksheet('DCF Calculator', {
		views: [{ showGridLines: false, state: 'frozen', xSplit: 1 }],   // freeze label column
	});
	ws.properties.defaultRowHeight = ROW_H;

	// Wide label column (A) + uniform data columns. Excel width is in character
	// units, so convert the target pixel width: px ≈ width*7 + 5  →  width = (px-5)/7
	const LABEL_COL_PX = 310;                 // column A (labels)
	const DATA_COL_PX  = 100;                       // change this one value to resize B…last
	ws.getColumn(1).width = (LABEL_COL_PX - 5) / 7;
	for (let c = 2; c <= lastCol; c++) ws.getColumn(c).width = (DATA_COL_PX - 5) / 7;

	// ── Cell helpers ───────────────────────────────────────────────────────────
	const gc = (ref: string) => ws.getCell(ref);
	const str = (ref: string, v: string)  => { gc(ref).value = v; };
	const num = (ref: string, v: number)  => { gc(ref).value = v; };
	const frm = (ref: string, f: string)  => { gc(ref).value = { formula: f, result: 0 } as ExcelJS.CellValue; };
	const fnt = (ref: string, opts: Partial<ExcelJS.Font>) => { gc(ref).font = { name: 'Arial', size: 11, color: { argb: BODYTEXT }, ...opts } as ExcelJS.Font; };
	const fil = (ref: string, argb: string) => { gc(ref).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb } } as ExcelJS.Fill; };
	const fmt = (ref: string, f: string)  => { gc(ref).numFmt = f; };
	const aln = (ref: string, h: ExcelJS.Alignment['horizontal']) => { gc(ref).alignment = { horizontal: h, vertical: 'middle' } as ExcelJS.Alignment; };

	// Section header: right-aligned brand-dark label, blue rule beneath header + atop first data row
	function sectionHdr(row: number, text: string) {
		ws.getRow(row).height = ROW_H;
		for (let ci = 1; ci <= lastCol; ci++) {
			const hc = ws.getRow(row).getCell(ci);
			hc.border = { ...(hc.border || {}), bottom: { style: 'medium', color: { argb: MIDBLUE } } } as ExcelJS.Borders;
			const nc = ws.getRow(row + 1).getCell(ci);
			nc.border = { ...(nc.border || {}), top: { style: 'medium', color: { argb: MIDBLUE } } } as ExcelJS.Borders;
		}
		str(`A${row}`, text);
		gc(`A${row}`).font      = { name: 'Arial', size: 11, bold: true, color: { argb: BRANDDARK } } as ExcelJS.Font;
		gc(`A${row}`).alignment = { horizontal: 'right', vertical: 'middle' } as ExcelJS.Alignment;
	}

	// Right-aligned row label in column A (units live in the label text)
	function lbl(row: number, text: string, bold = false) {
		str(`A${row}`, text);
		fnt(`A${row}`, { bold });
		aln(`A${row}`, 'right');
		ws.getRow(row).height = ROW_H;
	}

	// Period / quarter header cell — body-text bold (like subtotals), no fill, right-aligned
	function qHdr(ref: string, v: string | number) {
		if (typeof v === 'string') str(ref, v); else num(ref, v);
		fnt(ref, { bold: true });
		aln(ref, 'right');
	}

	// Blue-filled white-bold input cell
	function inp(ref: string, v: number, nf = MONEY) {
		num(ref, v);
		fil(ref, MIDBLUE);
		fnt(ref, { bold: true, color: { argb: WHITE } });
		fmt(ref, nf);
		aln(ref, 'right');
	}

	// Calculated formula cell
	function dat(ref: string, formula: string, nf: string, bold = false) {
		frm(ref, formula);
		fnt(ref, { bold });
		fmt(ref, nf);
		aln(ref, 'right');
	}

	// Headline KPI — orange fill on label + value cells only (not full row)
	function kpiLbl(row: number, text: string) {
		str(`A${row}`, text);
		gc(`A${row}`).font      = { name: 'Arial', size: 11, bold: true, color: { argb: WHITE } } as ExcelJS.Font;
		fil(`A${row}`, ORANGE);
		gc(`A${row}`).alignment = { horizontal: 'right', vertical: 'middle' } as ExcelJS.Alignment;
		ws.getRow(row).height = ROW_H;
	}
	function kpiVal(ref: string, formula: string, nf: string) {
		frm(ref, formula);
		gc(ref).font = { name: 'Arial', size: 11, bold: true, color: { argb: WHITE } } as ExcelJS.Font;
		fil(ref, ORANGE);
		fmt(ref, nf);
		aln(ref, 'right');
	}

	// ── Title ────────────────────────────────────────────────────────────────────
	str(`A${rTitle}`, `DCF / IRR Calculation — ${vesselName}`);
	gc(`A${rTitle}`).font      = { name: 'Arial', size: 18, bold: true, color: { argb: BRANDDARK } } as ExcelJS.Font;
	gc(`A${rTitle}`).alignment = { horizontal: 'left', vertical: 'middle' } as ExcelJS.Alignment;
	ws.getRow(rTitle).height = ROW_H;

	// Caption
	str(`A${rCaption}`, `Blue cells are inputs — adjust to run your own scenario.  Exported ${new Date().toLocaleDateString('en-GB')}.`);
	fnt(`A${rCaption}`, { italic: true });
	aln(`A${rCaption}`, 'left');

	// ── Logo ───────────────────────────────────────────────────────────────────
	if (logoSvg) {
		const png = await svgToPng(logoSvg, 350, 150);
		if (png) {
			const imgId = wb.addImage({ base64: png, extension: 'png' });
			// Right-align logo with the right edge of the data range (175px ÷ 78px ≈ 2.24 cols)
			const logoLeftCol = Math.max(2.5, lastCol - 1.10);
			ws.addImage(imgId, { tl: { col: logoLeftCol, row: 0.1 }, ext: { width: 175, height: 75 }, editAs: 'absolute' });
		}
	}

	// ── Inputs ─────────────────────────────────────────────────────────────────
	sectionHdr(rInputsHdr, 'Inputs');

	const inputDefs: [number, string, number, string][] = [
		[rPurchase,      'Purchase Price ($ Mn)',           params.purchase,      MONEY],
		[rSale,          'Sale Price ($ Mn)',               params.sale,          MONEY],
		[rEarnings,      'Average Earnings ($ k/Day)',      params.earnings,      MONEY],
		[rOpex,          'Daily Operating Costs ($ k/Day)', params.opex,          MONEY],
		[rCoe,           'Cost of Equity (%)',              params.coe,           PCT_IN],
		[rLtv,           'LTV (%)',                         params.ltv,           PCT_IN],
		[rBalloon,       'Balloon Payment ($ Mn)',          params.balloon,       MONEY],
		[rBaseRate,      'Base Rate (%)',                   params.baseRate,      PCT_IN],
		[rMargin,        'Interest Rate Margin (%)',        params.margin,        PCT_IN],
		[rOnHire,        'On Hire (Days / Year)',           params.onHire,        INT],
		[rOperatingDays, 'Operating Days (Days / Year)',    params.operatingDays, INT],
	];

	inputDefs.forEach(([row, label, value, nf]) => {
		lbl(row, label, true);
		inp(b(row), value, nf);
	});

	// Note alongside Interest Rate Margin
	str(`C${rMargin}`, '% (+ Base Rate)');
	fnt(`C${rMargin}`, { italic: true });
	aln(`C${rMargin}`, 'left');

	// ── Derived Values ─────────────────────────────────────────────────────────
	sectionHdr(rDerivedHdr, 'Derived Values');

	const derivedDefs: [number, string, string, string][] = [
		[rWacc,               'WACC (%)',                     `=(1-${b(rLtv)})*${b(rCoe)}+${b(rLtv)}*(${b(rBaseRate)}+${b(rMargin)})`,   PCT_CALC],
		[rQWacc,              'Quarterly WACC (%)',           `=(1+${b(rWacc)})^0.25-1`,                 PCT_CALC],
		[rQCoe,               'Quarterly Cost of Equity (%)', `=(1+${b(rCoe)})^0.25-1`,                  PCT_CALC],
		[rQIntRate,           'Quarterly Interest Rate (%)',  `=(${b(rBaseRate)}+${b(rMargin)})/4`,      PCT_CALC],
		[rLoanAmt,            'Loan Amount ($ Mn)',           `=${b(rLtv)}*${b(rPurchase)}`,             MONEY],
		[rEquityInv,          'Equity Invested ($ Mn)',       `=(1-${b(rLtv)})*${b(rPurchase)}`,         MONEY],
		[rQPrincipal,         'Quarterly Principal ($ Mn)',   `=(${b(rLoanAmt)}-${b(rBalloon)})/${nQ}`,  MONEY],
	];

	derivedDefs.forEach(([row, label, formula, nf]) => {
		lbl(row, label, true);
		dat(b(row), formula, nf);
	});

	// ── Quarterly Cash Flows ───────────────────────────────────────────────────
	sectionHdr(rCashHdr, 'Quarterly Cash Flows');

	// Period row
	qHdr(`A${rPeriod}`, 'Period');
	aln(`A${rPeriod}`, 'right');
	for (let t = 0; t <= nQ; t++) qHdr(qC(t) + rPeriod, t);
	ws.getRow(rPeriod).height = ROW_H;

	// Quarter labels
	qHdr(`A${rQuarter}`, 'Quarter');
	aln(`A${rQuarter}`, 'right');
	qHdr(`B${rQuarter}`, 'Now');
	for (let t = 1; t <= nQ; t++) qHdr(qC(t) + rQuarter, quarters[t - 1].label);
	ws.getRow(rQuarter).height = ROW_H;

	// Days in quarter
	lbl(rDays, 'Days in Quarter');
	for (let t = 1; t <= nQ; t++) {
		const c = qC(t) + rDays;
		num(c, quarters[t - 1].days);
		fnt(c, {});
		fmt(c, INT);
		aln(c, 'right');
	}

	// Revenue
	lbl(rRevenue, 'Revenue (earnings × on-hire days) ($ Mn)');
	for (let t = 1; t <= nQ; t++) dat(qC(t) + rRevenue, `=${ab(rEarnings)}*${qC(t)}${rDays}*${ab(rOnHire)}/365/1000`, MONEY_CF);

	// Operating Costs
	lbl(rOpCosts, 'Operating Costs ($ Mn)');
	for (let t = 1; t <= nQ; t++) dat(qC(t) + rOpCosts, `=-${ab(rOpex)}*${qC(t)}${rDays}*${ab(rOperatingDays)}/365/1000`, MONEY_CF);

	// Net Operating CF
	lbl(rNetOpCF, 'Net Operating Cash Flow ($ Mn)', true);
	for (let t = 1; t <= nQ; t++) dat(qC(t) + rNetOpCF, `=${qC(t)}${rRevenue}+${qC(t)}${rOpCosts}`, MONEY_CF, true);

	// Purchase / Sale
	lbl(rPurchSale, 'Purchase / Sale ($ Mn)');
	dat(b(rPurchSale), `=-${b(rPurchase)}`, MONEY_CF);
	dat(`${lastC}${rPurchSale}`, `=${b(rSale)}`, MONEY_CF);

	// Project CF
	lbl(rProjCF, 'Project Cash Flow ($ Mn)', true);
	dat(b(rProjCF), `=${b(rPurchSale)}`, MONEY_CF, true);
	for (let t = 1; t <= nQ; t++) dat(qC(t) + rProjCF, `=${qC(t)}${rNetOpCF}+${qC(t)}${rPurchSale}`, MONEY_CF, true);

	// Discount Factor WACC
	lbl(rDFWacc, 'Discount Factor (WACC) (%)');
	dat(b(rDFWacc), '=1', FACTOR);
	for (let t = 1; t <= nQ; t++) dat(qC(t) + rDFWacc, `=1/(1+${ab(rQWacc)})^${qC(t)}${rPeriod}`, FACTOR);

	// PV Project CF
	lbl(rPVProjCF, 'Present Value of Project Cash Flows ($ Mn)');
	dat(b(rPVProjCF), `=${b(rProjCF)}*${b(rDFWacc)}`, MONEY_CF);
	for (let t = 1; t <= nQ; t++) dat(qC(t) + rPVProjCF, `=${qC(t)}${rProjCF}*${qC(t)}${rDFWacc}`, MONEY_CF);

	// ── Project Results ────────────────────────────────────────────────────────
	sectionHdr(rProjHdr, 'Project Results');

	lbl(rProjNPV, 'Project Net Present Value ($ Mn)');
	dat(b(rProjNPV), `=SUM(B${rPVProjCF}:${lastC}${rPVProjCF})`, MONEY);

	kpiLbl(rProjDCF, 'Project DCF Value ($ Mn)');
	kpiVal(b(rProjDCF), `=${b(rProjNPV)}+${b(rPurchase)}`, MONEY);

	kpiLbl(rProjIRR, 'Project IRR (%)');
	kpiVal(b(rProjIRR), `=(1+IRR(B${rProjCF}:${lastC}${rProjCF}))^4-1`, PCT_CALC);

	// ── Financing ──────────────────────────────────────────────────────────────
	sectionHdr(rFinHdr, 'Financing');

	lbl(rLoanDraw, 'Loan Drawdown ($ Mn)');
	dat(b(rLoanDraw), `=${b(rLoanAmt)}`, MONEY_CF);

	lbl(rLoanOpen, 'Loan Opening Balance ($ Mn)');
	dat(`C${rLoanOpen}`, `=${b(rLoanAmt)}`, MONEY_CF);
	for (let t = 2; t <= nQ; t++) dat(qC(t) + rLoanOpen, `=${qC(t - 1)}${rLoanOpen}-${ab(rQPrincipal)}`, MONEY_CF);

	lbl(rInterest, 'Interest ($ Mn)');
	for (let t = 1; t <= nQ; t++) dat(qC(t) + rInterest, `=-${qC(t)}${rLoanOpen}*${ab(rQIntRate)}`, MONEY_CF);

	lbl(rPrincipalRepay, 'Principal Repayment ($ Mn)');
	for (let t = 1; t <= nQ; t++) dat(qC(t) + rPrincipalRepay, `=-${ab(rQPrincipal)}`, MONEY_CF);

	lbl(rBalloonPay, 'Balloon Payment ($ Mn)');
	dat(`${lastC}${rBalloonPay}`, `=-${ab(rBalloon)}`, MONEY_CF);

	lbl(rEquityCF, 'Equity Cash Flow ($ Mn)', true);
	dat(b(rEquityCF), `=${b(rProjCF)}+${b(rLoanDraw)}`, MONEY_CF, true);
	for (let t = 1; t <= nQ; t++) dat(qC(t) + rEquityCF, `=${qC(t)}${rProjCF}+${qC(t)}${rInterest}+${qC(t)}${rPrincipalRepay}+${qC(t)}${rBalloonPay}`, MONEY_CF, true);

	lbl(rDFCoe, 'Discount Factor (Cost of Equity) (%)');
	dat(b(rDFCoe), '=1', FACTOR);
	for (let t = 1; t <= nQ; t++) dat(qC(t) + rDFCoe, `=1/(1+${ab(rQCoe)})^${qC(t)}${rPeriod}`, FACTOR);

	lbl(rPVEquityCF, 'Present Value of Equity Cash Flows ($ Mn)');
	dat(b(rPVEquityCF), `=${b(rEquityCF)}*${b(rDFCoe)}`, MONEY_CF);
	for (let t = 1; t <= nQ; t++) dat(qC(t) + rPVEquityCF, `=${qC(t)}${rEquityCF}*${qC(t)}${rDFCoe}`, MONEY_CF);

	// ── Equity Results ─────────────────────────────────────────────────────────
	sectionHdr(rEquityHdr, 'Equity Results');

	lbl(rEquityNPV, 'Equity Net Present Value ($ Mn)');
	dat(b(rEquityNPV), `=SUM(B${rPVEquityCF}:${lastC}${rPVEquityCF})`, MONEY);

	kpiLbl(rEquityDCF, 'Equity DCF Value ($ Mn)');
	kpiVal(b(rEquityDCF), `=${b(rConstB)}/${b(rCoefA)}`, MONEY);

	kpiLbl(rEquityIRR, 'Equity IRR (%)');
	kpiVal(b(rEquityIRR), `=(1+IRR(B${rEquityCF}:${lastC}${rEquityCF}))^4-1`, PCT_CALC);

	// ── Analytical equity DCF intermediate terms ──────────────────────────────
	str(`A${rAnalHdr}`, 'Analytical Equity DCF — intermediate terms');
	fnt(`A${rAnalHdr}`, { bold: true, italic: true });
	aln(`A${rAnalHdr}`, 'right');
	ws.getRow(rAnalHdr).height = ROW_H;

	const terms: [number, string, string, string][] = [
		[rS1,    'S1  sum of DF(CoE)',         `=SUM(C${rDFCoe}:${lastC}${rDFCoe})`,                                                                  '0.0000'],
		[rS2,    'S2  sum of (t−1)×DF(CoE)',   `=SUMPRODUCT(C${rPeriod}:${lastC}${rPeriod}-1,C${rDFCoe}:${lastC}${rDFCoe})`,                           '0.0000'],
		[rO,     'O   PV of net operating CF', `=SUMPRODUCT(C${rNetOpCF}:${lastC}${rNetOpCF},C${rDFCoe}:${lastC}${rDFCoe})`,                           MONEY],
		[rCoefA, 'a   coefficient on price',   `=(1-${ab(rLtv)})+${ab(rQIntRate)}*${ab(rLtv)}*${b(rS1)}-${ab(rQIntRate)}*${ab(rLtv)}*${b(rS2)}/${nQ}+${ab(rLtv)}*${b(rS1)}/${nQ}`,   '0.000000'],
		[rConstB, 'b   constant term',         `=${b(rO)}+(${b(rSale)}-${b(rBalloon)})*${lastC}${rDFCoe}-${ab(rQIntRate)}*${b(rBalloon)}*${b(rS2)}/${nQ}+${b(rBalloon)}*${b(rS1)}/${nQ}`,   '0.000000'],
	];

	terms.forEach(([row, label, formula, nf]) => {
		lbl(row, label);
		fnt(`A${row}`, { italic: true });
		dat(b(row), formula, nf);
		fnt(`B${row}`, { italic: true });
	});

	// ── Download ───────────────────────────────────────────────────────────────
	const buffer = await wb.xlsx.writeBuffer();
	const blob = new Blob([buffer as ArrayBuffer], {
		type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
	});
	const a = document.createElement('a');
	a.href = URL.createObjectURL(blob);
	a.download = `DCF Calculator - ${vesselName.replace(/[^\w\s-]/g, '')}.xlsx`;
	document.body.appendChild(a);
	a.click();
	setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 500);
}
